import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapFinancePaymentRecordMutation,
  mapFinancePaymentRequestAttachment,
  mapFinancePaymentRequestDetail,
  mapFinancePaymentRequestFormContext,
  mapFinancePaymentRequestMutation,
  mapFinancePaymentRequestPage,
  mapFinancePaymentRequestPayment,
  mapFinancePaymentRequestResubmitMutation,
  mapFinancePaymentRequestSubmitResult,
  mapFinancePrepaymentItinerary,
  mapFinancePrepaymentSupplierOption,
  type FinancePaymentRequestAttachmentRaw,
  type FinancePaymentRequestDetailRaw,
  type FinancePaymentRequestFormContextRaw,
  type FinancePaymentRequestPageRaw,
  type FinancePaymentRequestPaymentRaw,
  type FinancePaymentRequestSubmitResultRaw,
  type FinancePaymentRequestWithdrawResultRaw,
  type FinancePrepaymentItineraryRaw,
  type FinancePrepaymentSupplierOptionRaw,
} from "../api/adapters";
import type {
  FinancePaymentRecordDraft,
  FinancePaymentRequestDraft,
  FinancePaymentRequestListScope,
  FinancePaymentRequestTarget,
} from "../types";

const endpoint = "/finance-payment-requests";

/** 取请款表单上方的当前应付、已请款、可请额度，以及我能选的部门。 */
export async function fetchFinancePaymentRequestFormContext(itineraryId: string, settlementSupplierId: string) {
  const response = await httpClient.get<ApiEnvelope<FinancePaymentRequestFormContextRaw>>(`${endpoint}/form-context`, {
    params: { itinerary_id: itineraryId, settlement_supplier_id: settlementSupplierId },
  });
  return mapFinancePaymentRequestFormContext(requireData(response.data));
}

/** 给其他结算主体发起预付款时按关键字搜结算主体（只列能当结算主体的）；关键字空白时不带 q。 */
export async function searchFinancePrepaymentSettlementSuppliers(keyword: string) {
  const q = keyword.trim();
  const response = await httpClient.get<ApiEnvelope<FinancePrepaymentSupplierOptionRaw[]>>(
    `${endpoint}/settlement-supplier-options`,
    { params: q ? { q } : {} },
  );
  return requireData(response.data).map(mapFinancePrepaymentSupplierOption);
}

/** 按团编码（精确匹配，只有已签约的团有）找要发起预付款的团；找不到为空数组。 */
export async function locateFinancePrepaymentItinerariesByTeamId(teamId: string) {
  const response = await httpClient.get<ApiEnvelope<FinancePrepaymentItineraryRaw[]>>(`${endpoint}/itineraries`, {
    params: { team_id: teamId.trim() },
  });
  return requireData(response.data).map(mapFinancePrepaymentItinerary);
}

/** 上传一个请款附件，拿到附件 ID；禁止自动重试非幂等写入。 */
export async function uploadFinancePaymentRequestAttachment(file: File) {
  return uploadFile(`${endpoint}/attachments`, file);
}

/** 上传一个付款回单，拿到附件 ID，登记已付时再带上；禁止自动重试非幂等写入。 */
export async function uploadFinancePaymentReceipt(file: File) {
  return uploadFile(`${endpoint}/payment-attachments`, file);
}

/** 用表单字段 file 上传一个文件，不自动重试。 */
async function uploadFile(url: string, file: File) {
  const form = new FormData();
  form.append("file", file);
  const response = await httpClient.post<ApiEnvelope<FinancePaymentRequestAttachmentRaw>>(url, form, {
    "axios-retry": { retries: 0 },
  });
  return mapFinancePaymentRequestAttachment(requireData(response.data));
}

/** 发起一笔请款并提交审批；禁止自动重试非幂等写入。 */
export async function submitFinancePaymentRequest(target: FinancePaymentRequestTarget, draft: FinancePaymentRequestDraft) {
  const response = await httpClient.post<ApiEnvelope<FinancePaymentRequestSubmitResultRaw>>(
    endpoint,
    mapFinancePaymentRequestMutation(target, draft),
    { "axios-retry": { retries: 0 } },
  );
  return mapFinancePaymentRequestSubmitResult(requireData(response.data));
}

/** 申请人本人撤回审批中的请款；能不能撤由后端（审批模块）判断，拦下时抛出原因。禁止自动重试非幂等写入。 */
export async function withdrawFinancePaymentRequest(requestId: string) {
  const response = await httpClient.post<ApiEnvelope<FinancePaymentRequestWithdrawResultRaw>>(
    `${endpoint}/${encodeURIComponent(requestId)}/withdraw`,
    undefined,
    { "axios-retry": { retries: 0 } },
  );
  requireData(response.data);
}

/** 被驳回或撤回后，申请人本人改完重新提交、开新一轮审批；团和结算主体不能改。禁止自动重试非幂等写入。 */
export async function resubmitFinancePaymentRequest(
  requestId: string,
  target: FinancePaymentRequestTarget,
  draft: FinancePaymentRequestDraft,
) {
  const response = await httpClient.post<ApiEnvelope<FinancePaymentRequestSubmitResultRaw>>(
    `${endpoint}/${encodeURIComponent(requestId)}/resubmit`,
    mapFinancePaymentRequestResubmitMutation(target, draft),
    { "axios-retry": { retries: 0 } },
  );
  return mapFinancePaymentRequestSubmitResult(requireData(response.data));
}

/** 读取请款记录的一页，按提交时间倒序；scope 是全部、待付款或我发起的。 */
export async function fetchFinancePaymentRequests(page: number, pageSize: number, scope: FinancePaymentRequestListScope) {
  const response = await httpClient.get<ApiEnvelope<FinancePaymentRequestPageRaw>>(endpoint, {
    params: { page, page_size: pageSize, scope },
  });
  return mapFinancePaymentRequestPage(requireData(response.data));
}

/** 读取一张请款单的详情。 */
export async function fetchFinancePaymentRequestDetail(requestId: string) {
  const response = await httpClient.get<ApiEnvelope<FinancePaymentRequestDetailRaw>>(
    `${endpoint}/${encodeURIComponent(requestId)}`,
  );
  return mapFinancePaymentRequestDetail(requireData(response.data));
}

/** 给一张审批已通过的请款登记已付；禁止自动重试非幂等写入。 */
export async function recordFinancePaymentRequestPayment(requestId: string, draft: FinancePaymentRecordDraft) {
  const response = await httpClient.post<ApiEnvelope<FinancePaymentRequestPaymentRaw>>(
    `${endpoint}/${encodeURIComponent(requestId)}/payments`,
    mapFinancePaymentRecordMutation(draft),
    { "axios-retry": { retries: 0 } },
  );
  return mapFinancePaymentRequestPayment(requireData(response.data));
}

/** 作废这张请款的一次已付登记，带上原因；禁止自动重试非幂等写入。 */
export async function voidFinancePaymentRequestPayment(requestId: string, paymentId: string, reason: string) {
  const response = await httpClient.post<ApiEnvelope<FinancePaymentRequestPaymentRaw>>(
    `${endpoint}/${encodeURIComponent(requestId)}/payments/${encodeURIComponent(paymentId)}/void`,
    { reason: reason.trim() },
    { "axios-retry": { retries: 0 } },
  );
  return mapFinancePaymentRequestPayment(requireData(response.data));
}

/** 带登录凭据下载请款附件（或付款回单）的二进制内容。 */
export async function downloadFinancePaymentRequestAttachment(requestId: string, attachmentId: string) {
  const response = await httpClient.get<Blob>(
    `${endpoint}/${encodeURIComponent(requestId)}/attachments/${encodeURIComponent(attachmentId)}/download`,
    { responseType: "blob" },
  );
  return response.data;
}

/** 校验统一响应，避免空载荷泄漏成属性读取错误。 */
function requireData<T>(envelope: ApiEnvelope<T>): T {
  if (envelope.code !== "OK" || envelope.data == null) throw new Error("请款接口返回了无效数据。");
  return envelope.data;
}
